import { dataFix } from 'global/common/dataFix';
import { sortObject } from 'global/common/sorting';
import { gEnums } from 'global/enums/globalEnums';
import { getTempSettingsState } from 'global/firestoreData/settings/ammendSettings';
import { allowApply, assignColors, getMenuItemMessage, menuItemAllowances, updateMenuItem } from 'global/viewSettings/components/menus/menuItemHelpers';
import { pageItemActionTypes } from 'global/viewSettings/enums/itemActionTypes';
import { getSwipeProps } from 'global/viewSettings/helpers/viewSettingsHelpers';
import _ from 'lodash';
import { useContext, useMemo } from 'react';
import { SettingParentContext } from '../SettingParentContext';
import { SettingsAreaContext } from '../SettingsAreaContext';
import { ViewSettingsComponentContext } from '../ViewSettingsComponentContext';
import { ViewSettingsContext } from '../ViewSettingsContext';
import { useAppUserAccess } from './useAppUserAccess';
import { useMutable } from './useMutable';
import { useTempSettings } from './useTempSettings';

const _allowedSortProps = ['viewItems', 'props']

export const useVcmProps = (props) => {

  const {
    activeData,
    getColors,
    menuTypeKey,
    propsOnly,
  } = props

  const settingsParentContext = useContext(SettingParentContext)
  const { settings, states } = settingsParentContext ?? {}
  const { global_state, baseSettings_state, framework_state, paps_state } = states ?? {}
  const { desktopMode } = framework_state ?? {}
  const { pathViews, appArea } = paps_state
  const { eventSettings } = settings ?? {}
  const { global } = eventSettings ?? {}
  const { parentDefaults } = global ?? {}
  const appUserAccess = useAppUserAccess()

  // settingsAreaContext
  const settingsAreaContext = useContext(SettingsAreaContext);
  const { settingsArea_state, settingsArea_fns } = settingsAreaContext ?? {}

  // viewSettingsSwipeContext 
  const viewSettingsContext = useContext(ViewSettingsContext);
  const { viewSettings_state, viewSettings_handlers, viewSettings_fns } = viewSettingsContext ?? {}
  const { settings_temp, settingsStatus } = viewSettings_state ?? {}

  // viewSettingsComponentContext
  const viewSettingsComponentContext = useContext(ViewSettingsComponentContext);
  const { vsc_state } = viewSettingsComponentContext ?? {}
  const { isGlobal, data_current, updateProps, swipedItem } = vsc_state ?? {}
  const isGlobalPageItem = false

  const { value: _settings_temp } = useMutable(settings_temp)

  const { _global } = useTempSettings(settings_temp, isGlobal)
  const { _viewItems } = getTempSettingsState(settings_temp, isGlobal)

  const _swipedItem = _.cloneDeep(swipedItem)

  const _vscProps = getSwipeProps(
    data_current,
    viewSettings_handlers,
    _swipedItem,
    updateProps,
    _.cloneDeep(_viewItems),
    settingsArea_fns,
    isGlobal,
    appArea
  )
  const { viewProps } = _vscProps ?? {}

  const { themeColors } = _global ?? {}

  const { menuItems, menuType, otherData, settingsMessage } = getInitMenuData({
    isGlobalPageItem,
    menuTypeKey,
    updateProps,
    parentDefaults,
    currentViewItems: _viewItems,
    viewProps,
    global_state
  })

  const { baseSettings } = baseSettings_state ?? {}
  const { adjustedPermissions } = baseSettings ?? {}
  const { global: global_prm, page: page_prm, pageItem: pageItem_prm } = adjustedPermissions ?? {}
  const { projectModules } = settingsArea_state ?? {}

  let activeMenuItems = []

  const miProps = {
    activeData: data_current,
    settingsArea_state,
    baseSettings_state,
    data_current,
    desktopMode,
    getColors: false,
    isGlobal,
    menuItems,
    menuType,
    menuTypeKey,
    pathViews,
    propsOnly,
    themeColors,
  }

  const vcmProps = useMemo(() => {

    if (menuItems) {

      if (_.isArray(menuItems)) {

        const grpSettings = global_prm[menuTypeKey] ? global_prm[menuTypeKey] : {}

        // most likely sub or child items 
        menuItems.forEach(item => {
          if (item) {

            let allowItem = false

            if (pageItemActionTypes[item]) {
              const { allowQuestionsAndAnswers, allowVideoConferencing } = projectModules ?? {}
              switch (item) {
                case pageItemActionTypes.questionsAndAnswers:
                  allowItem = allowQuestionsAndAnswers
                  break;
                case pageItemActionTypes.videoConferencing:
                  allowItem = allowVideoConferencing
                  break;
                default:
                  allowItem = true
                // nothing
              }
            } else {
              allowItem = true
            }

            if (allowItem && !grpSettings.restrict || (appUserAccess && (appUserAccess.settingsAuthLevel === gEnums.accessLevels.superAdmin.value))) {
              const { restrict } = grpSettings
              let caption = _.startCase(item)
              const i = {
                caption,
                key: menuTypeKey,
                settingKey: item,
                parentSettingKey: item,
                subKey: item,
                show: true,
                showDesktop: true,
                restrict,
                propSection: item.propSection,
                ...assignColors(data_current, themeColors, menuType, getColors, item, true)
              }
              if (data_current[item]) { i.applied = data_current[item].apply }
              if (data_current && data_current[item] && data_current[item]) {
                if (data_current[item].allowAction) { i.isActive = true }
                if (data_current[item].apply) { i.isActive = true }
                if (data_current[item].useTransitions) { i.isActive = true }
              }
              activeMenuItems.push(i)
            }
          }
        })
      } else {

        // the menuItems here are the pageItems/viewItems 
        Object.keys(menuItems).forEach(key => {

          let grpSettings;

          if (page_prm[key]) {
            grpSettings = _.cloneDeep(page_prm[key]);
          } else if (pageItem_prm[key]) {
            grpSettings = _.cloneDeep(pageItem_prm[key]);
          } else {
            grpSettings = {};
          }

          const { componentAreas } = grpSettings ?? {}
          const { pageAreaTypes } = componentAreas ?? {}

          if (!grpSettings.caption) { grpSettings.caption = _.startCase(key) }

          let allow = true

          if (grpSettings) {

            if (pageAreaTypes && !pageAreaTypes.includes('all')) {
              if (isGlobal) {
                if (pageAreaTypes && !pageAreaTypes.includes('global')) {
                  allow = false
                }
              } else {
                if (pageAreaTypes && !pageAreaTypes.includes('page')) {
                  allow = false
                }
              }
            }

            if (grpSettings.restrict) {
              if (!appUserAccess) {
                allow = false
              } else {
                if (appUserAccess.settingsAuthLevel < gEnums.accessLevels.admin.value) {
                  allow = false
                }
              }
            }

            if (grpSettings.settingsAuthLevel) {
              if (grpSettings.settingsAuthLevel > appUserAccess.settingsAuthLevel) {
                allow = false
              }
            }

          }

          if (allow || _allowAll) {
            activeMenuItems.push(getMenuItem(desktopMode, miProps, key, null, grpSettings, activeData))
          }
        })
      }
    }

    switch (menuType) {
      case gEnums.menuTypes.viewItem:
      case gEnums.menuTypes.viewItemData:
        if (isGlobal) { activeMenuItems = _.sortBy(activeMenuItems, 'key') }
        break;

      case gEnums.menuTypes.propItem:
        if (propsOnly) { activeMenuItems = _.sortBy(activeMenuItems, 'key') }
        break;
      default:
      // nothing
    }

    _.forEach(activeMenuItems, (ami) => {
      updateMenuItem(ami, menuType)
    })


    const _activeMenuItems = dataFix.deepClean(activeMenuItems)
    const menuItemAllows = menuItemAllowances(menuType, isGlobal)

    const allowSort = _allowedSortProps.includes(menuTypeKey)

    return {
      activeMenuItems: _activeMenuItems,
      menuItemAllows,
      propsOnly,
      allowSort,
      menuTypeKey,
      viewItems: _viewItems,
      data_current,
      swipedItem: _swipedItem,
      updateProps,
      menuItems,
      menuType,
      settingsMessage,
      otherData
    }
  }, [appArea, settings_temp, settingsStatus])

  return vcmProps

};


const getInitMenuData = (props) => {

  const {
    currentViewItems,
    isGlobalPageItem,
    menuTypeKey,
    updateProps,
    parentDefaults,
    viewProps,
    global_state
  } = props

  const { useClientThemes, useClientSignIn, useHomeTransitions, groupingOptions } = parentDefaults ?? {}
  const { useGlobalGroupSettings, useGlobalAlphabetSettings } = groupingOptions ?? {}

  let menuItems;
  let menuType;
  let otherData;
  let settingsMessage;

  switch (menuTypeKey) {
    case gEnums.viewSettingTypes.propSections:
      const upsP = { ...updateProps }
      upsP.formKey = 'props'
      // let propSectionData = viewSettings_fns.getSettingsValue(updateProps, isGlobalPageItem, currentViewItems)
      let propSectionData = getSettingsValue(global_state, updateProps, isGlobalPageItem, currentViewItems)
      menuItems = sortObject(propSectionData, 'position')
      menuType = gEnums.menuTypes.propSections
      break;

    case gEnums.viewSettingTypes.globalPageItems: // stay here
    case gEnums.viewSettingTypes.views: // stay here
    case gEnums.viewSettingTypes.viewItems: // stay here 
      let vis = viewProps
      if (vis) { menuItems = sortObject(vis, 'position') }
      menuType = gEnums.menuTypes.viewItem
      break;

    case gEnums.viewSettingTypes.props:
      // let propData = viewSettings_fns.getSettingsValue(updateProps, isGlobalPageItem, currentViewItems)
      let propData = getSettingsValue(global_state, updateProps, isGlobalPageItem, currentViewItems)
      menuItems = sortObject(propData, 'position')
      const upsSections = { ...updateProps }
      upsSections.formKey = 'propSections'
      // otherData = viewSettings_fns.getSettingsValue(upsSections, isGlobalPageItem, currentViewItems)
      otherData = getSettingsValue(global_state, upsSections, isGlobalPageItem, currentViewItems)
      // console.log('other_data', other_data)
      menuType = gEnums.menuTypes.propItem
      break;

    case gEnums.viewSettingTypes.propSection:
      menuItems = viewProps
      menuType = gEnums.menuTypes.propSections // menuTypeKey
      break;

    case gEnums.viewSettingTypes.appData:
      menuItems = currentViewItems
      menuType = gEnums.menuTypes.viewItemData
      break;

    case gEnums.viewSettingTypes.profiles:
      menuItems = viewProps
      menuType = gEnums.menuTypes.block
      break;

    default:
      menuItems = viewProps
      menuType = gEnums.menuTypes.block
    // nothing
  }

  settingsMessage = getMenuItemMessage(menuTypeKey, { useClientSignIn, useClientThemes, useHomeTransitions, useGlobalGroupSettings, useGlobalAlphabetSettings })

  switch (menuTypeKey) {
    case 'style':
    case 'styles':
    case 'themedColors':
    case 'themedItems':
      menuType = gEnums.menuTypes.applyColor
      break;
    default:
    // nothing
  }

  return { menuItems, menuType, settingsMessage, otherData }
}


/**
 * 
 * @param {boolean} desktopMode 
 * @param {object} props 
 * @param {string} key 
 * @param {string} otherKey 
 * @param {object} grpSettings 
 * @param {object} activeData 
 * @returns an object for the menuItem set up for the element to read
 */
const getMenuItem = (desktopMode, props, key, otherKey, grpSettings, activeData) => {

  const { menuItems, menuTypeKey, data_current, menuType, themeColors, getColors } = props

  const menuItem = menuItems?.[key]

  const { restrictAdd, adminOnly, remove, show, showDesktop, tagged, schedule, header, cssed, drilled, secret, pin, propSection, applied, display: display_item } = menuItem ?? {}

  const iconProps = { adminOnly, cssed, drilled, header, pin, schedule, secret, show, tagged, remove, restrictAdd }

  // remove the undefined
  _.forEach(iconProps, (iconProp, key) => {
    if (iconProp === undefined) {
      delete iconProps[key]
    }
  })

  let { caption, defaultIcon } = display_item ?? {}

  const _show = desktopMode ? showDesktop : show
  let isActive;

  if (activeData && activeData[key] && Object.keys(activeData[key]).length > 0) {
    switch (key) {
      case 'dataManagement':
      case 'dataModifications':
      case 'pageItemActions':
      case 'permissions':
        const activeAllows = _.find(activeData[key], { 'allowAction': true })
        if (activeAllows) isActive = true
        break;
      default:
        isActive = true
    }
  }

  const { restrict, settingsAuthLevel, isAlt } = grpSettings

  if (!caption) { caption = grpSettings.caption }
  if (menuItem.displayCaption) { caption = _.startCase(menuItem.displayCaption) }

  switch (menuTypeKey) {
    case 'viewItems':
      if (caption !== _.startCase(key)) { caption += ' (' + _.startCase(key) + ')' }
      break;
    case 'views':
      break;
    default:
    // nothing
  }

  const showItem = allowApply(menuType) ? applied : _show
  const otherKeyValue = otherKey ? menuItem[otherKey] : null

  const _menuItem = {
    isAlt,
    adminOnly,
    caption,
    icon: defaultIcon ? defaultIcon : null,
    isActive,
    key: otherKeyValue ? otherKeyValue : key,
    propSection,
    restrict,
    restrictAdd,
    secret,
    settingsAuthLevel,
    show: showItem,
    showDesktop: showItem,
    iconProps,
    subProps: { ...iconProps },
    ...assignColors(data_current, themeColors, menuType, getColors, menuItem)
  }

  return _menuItem

}

const getSettingsValue = (global_state, updateProps, isGlobalPageItem, currentViewItems) => {

  const getVis = (settings_temp) => {
    return settings_temp
    if (settings_temp) {
      const { viewItems } = settings_temp ?? {}
      return viewItems
    }
  }

  const { settings_globalPageItems } = global_state ?? {}
  const settings_temp_global = settings_globalPageItems ? _.cloneDeep(settings_globalPageItems) : null

  let cd = {}
  let viewItems = isGlobalPageItem && settings_temp_global ? getVis(settings_temp_global) : currentViewItems //_viewItems
  const { formKey, subKey, vit, pit, dataListType } = updateProps ?? {}

  if (formKey) {
    if (vit) {
      const _viewItem = viewItems && viewItems[vit] ? viewItems[vit] : {}
      if (pit) {
        const pi = _viewItem.props && _viewItem.props[pit] ? _viewItem.props[pit] : {}
        cd = pi[formKey] ? pi[formKey] : {}
        cd = subKey ? cd[subKey] : cd
      } else {
        cd = _viewItem[formKey] ? _viewItem[formKey] : {}
        cd = subKey ? cd[subKey] : cd
      }
    } else {
      console.log('?????????????')
      // if (_global) {
      //   if (dataListType) {
      //     cd = _global?.[formKey] ? _global[formKey] : {}
      //     cd = dataListType ? cd[dataListType] : cd
      //   } else {
      //     cd = _global?.[formKey] ? _global[formKey] : {}
      //     cd = subKey ? cd[subKey] : cd
      //   }
      // }
    }
  }
  return cd
}